// Milepost strips: one collapsible block per corridor, or per road when unified.
// Top line: multi-deficiency cluster rings. Next: the corridor's intersections
// (diamonds; filled in the severity colour when the sight distance is short).
// Then one lane per selected analysis; hatched gaps are unsurveyed stretches between a
// road's corridors. Click a site → the map jumps to it; double-click → its report.
import React, { useState } from 'react';
import { ANALYSIS_COLORS, ANALYSIS_NAMES, SEVERITY_COLORS } from './overviewModel';

const W = 1000;
const LEFT = 44;
const RIGHT = 10;
const LANE = 14;
const IX_ROW = 16;

const fmt = (v) => Number(v).toFixed(2);

function tickStep(span) {
  if (span > 40) return 10;
  if (span > 16) return 5;
  if (span > 6) return 2;
  if (span > 2.5) return 1;
  return 0.5;
}

function Strip({ block, sites, clusters, intersections, analyses, colourBy, axis, onFocus }) {
  const span = Math.max(block.hi - block.lo, 1e-6);
  const x = (v) => LEFT + ((v - block.lo) / span) * (W - LEFT - RIGHT);
  const top = 18 + (intersections.length ? IX_ROW : 0);      // first analysis lane
  const height = top + 2 + analyses.length * LANE + 22;
  const base = top + analyses.length * LANE;
  const partOf = new Map(block.parts.map((p) => [p.seg.key, p]));
  const at = (segKey, mp) => mp + (partOf.get(segKey)?.offset || 0);
  const step = tickStep(span);
  const ticks = [];
  for (let t = Math.ceil(block.lo / step) * step; t <= block.hi + 1e-9; t += step) ticks.push(t);
  const showMileage = block.axisKind === 'mi' || axis === 'mi';
  const hatch = `ov-hatch-${block.key.replace(/\W/g, '')}`;
  const color = (s) => (colourBy === 'severity' ? SEVERITY_COLORS[s.severity] : ANALYSIS_COLORS[s.analysis]);

  return (
    <div className="ov-strip-scroll">
      <svg viewBox={`0 0 ${W} ${height}`} role="img" aria-label={`Milepost strip of ${block.title}`}>
        <defs>
          <pattern id={hatch} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="#0b1222" /><rect width="2" height="6" fill="#1e293b" />
          </pattern>
        </defs>
        {intersections.length > 0 && (
          <g>
            <text x="4" y={18 + 10} className="ov-strip-label"><title>Intersections surveyed for sight distance</title>INT</text>
            {block.parts.map((p) => (
              <line key={p.seg.key} x1={x(p.lo)} x2={x(p.hi)} y1={18 + 7} y2={18 + 7} stroke="#1e293b" />
            ))}
            {intersections.map((ix) => {
              const cx = x(at(ix.segKey, ix.mp));
              const cy = 18 + 7;
              const short = Boolean(ix.severity);
              return (
                <path key={ix.id} d={`M${cx},${cy - 5.5} L${cx + 5.5},${cy} L${cx},${cy + 5.5} L${cx - 5.5},${cy} Z`}
                  className="ov-strip-ix" fill={short ? SEVERITY_COLORS[ix.severity] : '#0b1222'}
                  stroke={short ? '#f8fafc' : ANALYSIS_COLORS.ISD} strokeWidth={short ? 0.8 : 1.4}
                  onClick={() => onFocus(ix)} onDoubleClick={() => ix.reportUrl && window.open(ix.reportUrl, '_blank', 'noopener')}>
                  <title>{`Intersection ${ix.name || ''} · MP ${fmt(ix.mp)}${ix.isdFt ? ` · required sight distance ${Math.round(ix.isdFt)} ft` : ''}${ix.quality != null ? ` · quality ${(ix.quality * 100).toFixed(0)}%` : ''}${short ? ` · ${ix.severity}` : ' · sight distance met'}
Click: show on map · Double-click: open the ISD report`}</title>
                </path>
              );
            })}
          </g>
        )}
        {analyses.map((a, i) => {
          const y = top + i * LANE;
          return (
            <g key={a}>
              <text x="4" y={y + 9.5} className="ov-strip-label"><title>{ANALYSIS_NAMES[a]}</title>{a}</text>
              {block.parts.map((p) => (
                <rect key={p.seg.key} x={x(p.lo)} y={y} width={Math.max(0, x(p.hi) - x(p.lo))} height={LANE - 3} fill="#09101b" />
              ))}
              {sites.filter((s) => s.analysis === a && s.mpStart != null).map((s) => {
                const x0 = x(at(s.segKey, s.mpStart));
                const w = Math.max(3, x(at(s.segKey, s.mpEnd != null ? s.mpEnd : s.mpStart)) - x0);
                return (
                  <rect key={s.id} x={x0} y={y} width={w} height={LANE - 3} fill={color(s)} className="ov-strip-site"
                    onClick={() => onFocus(s)} onDoubleClick={() => s.reportUrl && window.open(s.reportUrl, '_blank', 'noopener')}>
                    <title>{`${a} · MP ${fmt(s.mpStart)}${s.lengthMi ? `–${fmt(s.mpEnd)}` : ''} · ${s.severity} · ${s.worstText || ''}\nClick: show on map · Double-click: open report`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}
        {block.parts.slice(1).map((p, i) => {
          const prev = block.parts[i];
          const gap = x(p.lo) - x(prev.hi);
          return gap > 0.5 ? (
            <rect key={`gap-${p.seg.key}`} x={x(prev.hi)} y={16} width={gap} height={base - 16} fill={`url(#${hatch})`}>
              <title>Not surveyed</title>
            </rect>
          ) : <line key={`cut-${p.seg.key}`} x1={x(p.lo)} x2={x(p.lo)} y1={14} y2={base} stroke="#64748b" strokeDasharray="2 2" />;
        })}
        {clusters.map((c) => {
          // a capsule over EXACTLY the overlapping stretch (a ring when it is very short)
          const x0 = x(at(c.segKey, c.mpStart));
          const x1 = x(at(c.segKey, c.mpEnd));
          const w = Math.max(10, x1 - x0);
          return (
            <rect key={c.id} x={(x0 + x1) / 2 - w / 2} y={4} width={w} height={10} rx={5} className="ov-strip-cluster"
              onClick={() => onFocus(c)}>
              <title>{`${c.n} analyses overlap (${c.analyses.join(', ')}) · MP ${fmt(c.mpStart)}–${fmt(c.mpEnd)}`}</title>
            </rect>
          );
        })}
        <line x1={LEFT} x2={W - RIGHT} y1={base} y2={base} stroke="#334155" />
        {ticks.map((t) => (
          <g key={t}>
            <line x1={x(t)} x2={x(t)} y1={base} y2={base + 4} stroke="#334155" />
            <text x={x(t)} y={base + 15} textAnchor="middle" className="ov-strip-tick">
              {showMileage ? `${(t - (block.axisKind === 'mi' ? 0 : block.lo)).toFixed(1)} mi` : `MP ${Number(t.toFixed(2))}`}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function OverviewStrips({ blocks, sites, clusters, analyses, colourBy, axis, onFocus }) {
  const [collapsed, setCollapsed] = useState({});
  if (!blocks.length) return <p className="safety-muted">Select at least one road.</p>;
  if (!analyses.length) return <p className="safety-muted">Select at least one analysis.</p>;
  return (
    <div>
      {blocks.map((b) => {
        const keys = new Set(b.parts.map((p) => p.seg.key));
        const mine = sites.filter((s) => keys.has(s.segKey));
        const ixs = b.parts.flatMap((p) => (p.seg.intersections || []).map((ix) => ({ ...ix, segKey: p.seg.key })));
        // a road with nothing to show for the chosen analyses starts collapsed
        const open = collapsed[b.key] === undefined ? mine.length > 0 : !collapsed[b.key];
        const projects = Array.from(new Set(b.parts.map((p) => p.seg.projectId)));
        return (
          <div className="ov-strip-block" key={b.key}>
            <button type="button" className="ov-strip-head" aria-expanded={open}
              onClick={() => setCollapsed((c) => ({ ...c, [b.key]: open }))} title={open ? 'Collapse' : (mine.length ? 'Expand' : 'No sites for the chosen analyses · expand anyway')}>
              <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>
              <span className="ov-strip-title">{b.title}</span>
              <span className="ov-strip-meta">
                {b.parts.map((p) => `MP ${(p.seg.mpMin || 0).toFixed(1)}–${(p.seg.mpMax || 0).toFixed(1)}`).join(' · ')}
                {' · '}{mine.length} sites{ixs.length ? ` · ${ixs.length} intersection${ixs.length === 1 ? '' : 's'}` : ''} · project{projects.length > 1 ? 's' : ''} {projects.map((p) => `#${p}`).join(', ')}
                {b.axisKind === 'mi' && b.parts.length > 1 ? ' · mileposts overlap, shown end to end as mileage' : ''}
              </span>
            </button>
            {open && (
              <Strip block={b} sites={mine} intersections={ixs} clusters={clusters.filter((c) => keys.has(c.segKey))}
                analyses={analyses} colourBy={colourBy} axis={axis} onFocus={onFocus} />
            )}
          </div>
        );
      })}
    </div>
  );
}
